
import { Investor } from "@/types/investor";
import { useSaveInvestorData } from "./useSaveInvestorData";
import { useRelevanceOperations } from "./useRelevanceOperations";
import { useSingleProcessingOperations } from "./useSingleProcessingOperations";
import { useBatchOperations } from "./useBatchOperations";
import { useDataResetOperations } from "./useDataResetOperations";

/**
 * Hook for handling investor processing operations
 * This is now a composition of more specialized hooks
 */
export const useProcessingOperations = (
  investors: Investor[],
  setInvestors: (investors: Investor[]) => void,
  processedIds: Set<string>,
  setProcessedIds: (processedIds: Set<string>) => void,
  selectedInvestor: Investor | null,
  setSelectedInvestor: (investor: Investor | null) => void,
  contacts: any[] // Using any[] to avoid circular dependencies, but this is the Contact[] type
) => {
  const { 
    saveDataToSupabaseAndLocal, 
    flushPendingSaves,
    startBatchProcessing,
    endBatchProcessing,
    isSaving 
  } = useSaveInvestorData();
  
  // Use specialized hooks for different operations
  const {
    isAnalyzing,
    handleRelevanceSubmit
  } = useRelevanceOperations(
    investors,
    setInvestors,
    processedIds,
    setProcessedIds,
    contacts
  );
  
  const {
    processingIds,
    isEnriching,
    setIsEnriching,
    handleProcessInvestor,
    handleEnrichInvestor,
    handleInvestorUpdate
  } = useSingleProcessingOperations(
    investors,
    setInvestors,
    processedIds,
    setProcessedIds,
    selectedInvestor,
    setSelectedInvestor,
    contacts
  );
  
  const {
    isProcessingBatch,
    handleProcessBatch
  } = useBatchOperations(
    investors,
    setInvestors,
    processedIds,
    setProcessedIds,
    contacts,
    () => flushPendingSaves(investors, contacts, processedIds),
    startBatchProcessing,
    endBatchProcessing
  );
  
  const {
    handleReprocess
  } = useDataResetOperations(
    setInvestors,
    setProcessedIds,
    saveDataToSupabaseAndLocal
  );

  return {
    // AI processing states
    isAnalyzing,
    isProcessingBatch,
    isEnriching,
    setIsEnriching,
    processingIds,
    
    // Processing operations
    handleRelevanceSubmit,
    handleProcessInvestor,
    handleEnrichInvestor,
    handleProcessBatch,
    handleInvestorUpdate,
    handleReprocess,
  };
};
